import { useCallback, useMemo, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { useLatencyBustPoll } from '@frontend/payments-hooks';
import { BaseFieldProps, ButtonProps, useFormField } from '@frontend/design-system';
import { usePaymentsPlatformTrackingIds } from '../../../utils/use-payments-platform-tracking-ids';
import { CardOnFileProps } from '../../card-on-file';
import { CardOnFileStripeApi } from './use-card-on-file-stripe-api';

export type CardOnFileStripeValue = {
  cardReadyToSubmit: boolean;
  setCardReadyToSubmit: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
  isSetupLinkLoading: boolean;
  isFetchedLinkLoading: boolean;
  saveCardOnFile: () => Promise<void>;
  stripe: Stripe | null;
  elements: StripeElements | null;
  nameFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
};

export type UseCardOnFileStripeProps = CardOnFileProps & {
  api: CardOnFileStripeApi;
};

export const useCardOnFileStripe = ({
  onCardSaved,
  onCancel,
  onError,
  personId,
  locationId,
  api,
}: UseCardOnFileStripeProps) => {
  const { t } = useTranslation('payments');
  const stripe = useStripe();
  const elements = useElements();

  const nameFieldProps = useFormField({ type: 'text', required: true });

  const [cardReadyToSubmit, setCardReadyToSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: setupLink,
    isLoading: isSetupLinkLoading,
    isFetched: isFetchedLinkLoading,
  } = api.useGetPaymentMethodSetupLink(
    {
      locationId,
    },
    {
      enabled: !!locationId,
    }
  );

  const { data: paymentMethodsData, refetch: refetchPaymentMethods } = api.useGetPaymentMethodsByPerson({
    locationId,
    patientId: personId ?? '',
  });

  const paymentMethods = paymentMethodsData || [];

  const { getPollPromise } = useLatencyBustPoll({
    initialTimeoutIncrease: 100,
    queryMethod: async () => {
      const { data } = await refetchPaymentMethods();
      return data;
    },
    onPollEnd: () => {
      setIsSubmitting(false);
    },
    accessor: (data) => {
      return data?.length;
    },
  });

  const saveCardOnFile = useCallback(async () => {
    if (!stripe || !elements || !setupLink) {
      console.error('Stripe has not loaded yet');
      // Stripe.js has not yet loaded.
      return;
    }

    if (!api.isUserAuthenticated()) {
      onError?.(t('Weave users do not have access to save card.'));
      return;
    }

    setIsSubmitting(true);
    const { error: submitError } = await elements.submit();

    if (submitError) {
      onError?.(t('Error submitting details to Stripe. Please review information and try again.'), submitError);
    }
    // get the client secret
    let clientSecret: string | undefined = '';

    try {
      const clientSecretLink = await api.getClientSecretLink(setupLink, {
        data: {
          personId,
          locationId,
        },
      });

      const setupResponse = await api.getClientSecret(clientSecretLink || '', locationId);

      clientSecret = setupResponse?.clientSecret;
      if (clientSecret) {
        const result = await stripe.confirmSetup({
          elements,
          clientSecret,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${window.location.href}`,
            payment_method_data: {
              billing_details: {
                name: nameFieldProps.value,
              },
            },
          },
        });

        if (result.error) {
          setIsSubmitting(false);

          onError?.(
            t('Unable to save Card. Please try again. {{errorCode}}', {
              errorCode: result.error.code,
            }),
            result.error
          );
        } else {
          const pms = await getPollPromise(paymentMethods);

          const found =
            pms?.find((pm) => pm.id === result.setupIntent?.payment_method) ??
            ({
              type: 'card',
              id: result.setupIntent?.payment_method,
              is_default: true,
            } as PaymentMethod);

          onCardSaved(found);
        }
      } else {
        throw new Error('Client secret not found');
      }
    } catch (e) {
      if (e instanceof Error) {
        onError?.(t('Error occured while saving Card.'), e);
      } else {
        onError?.(t('Unknown error occured while saving Card.'));
      }

      setIsSubmitting(false);
    }
  }, [stripe, elements, setupLink, api, onError, onCardSaved]);

  const { primaryBtn, secondaryBtn } = usePaymentsPlatformTrackingIds('card-on-file', {
    primaryBtn: 'save-card',
    secondaryBtn: 'cancel',
  });

  /**
   * Component Props
   */
  const primary = useMemo<ButtonProps>(() => {
    return {
      disabled: isSubmitting || nameFieldProps['aria-invalid'] || !nameFieldProps.value || !cardReadyToSubmit,
      onClick: saveCardOnFile,
      trackingId: primaryBtn,
    };
  }, [nameFieldProps, cardReadyToSubmit, saveCardOnFile, isSubmitting]);

  const secondary = useMemo<ButtonProps>(() => {
    return {
      onClick: () => onCancel?.(),
      trackingId: secondaryBtn,
      disabled: isSubmitting,
    };
  }, [onCancel]);

  const shouldShowFooter = !!personId;

  const shouldShowLoading = !personId || isSubmitting || isSetupLinkLoading || !isFetchedLinkLoading;

  return {
    isSubmitting,
    isSetupLinkLoading,
    isFetchedLinkLoading,
    cardReadyToSubmit,
    setCardReadyToSubmit,
    saveCardOnFile,
    stripe,
    elements,
    nameFieldProps,

    // component props
    primary,
    secondary,
    shouldShowFooter,
    shouldShowLoading,
    onSubmit: saveCardOnFile,
    onCancel,
  };
};
